// Hands the Cognito tokens to the turnus browser extension after it opened the
// login page with ?extension=true.
//
// Chrome/Edge: chrome.runtime.sendMessage(extensionId, ...) via the extension's
// externally_connectable entry. Only the extension receives the message; nothing
// else on the page can read it.
// Firefox (web pages cannot message an extension there), older extension
// versions, and IDs not on the allowlist: window.postMessage to this exact
// origin, picked up by the extension's auth content script. Scripts on the page
// can still see that message, which is why GTM is not loaded on the extension
// login page (see Authentication.tsx).

// Chrome Web Store ID. Unpacked builds get the same ID from the manifest `key`.
// Add the Edge Add-ons store ID here once known; until then Edge store installs
// use the postMessage fallback.
export const TURNUS_EXTENSION_IDS: readonly string[] = [
  "jjclaofbfkpmnaobblngbfabnapmfjln",
]

export interface ExtensionAuthPayload {
  tokens: {
    idToken: string
    accessToken: string
    refreshToken: string
  }
}

export type ExtensionHandoffChannel = "externally_connectable" | "postMessage"

interface ChromeRuntimeForWebPages {
  sendMessage: (
    extensionId: string,
    message: unknown,
    responseCallback: (response: unknown) => void,
  ) => void
  lastError?: { message?: string }
}

const getChromeRuntime = (): ChromeRuntimeForWebPages | undefined => {
  const runtime = (
    window as unknown as { chrome?: { runtime?: ChromeRuntimeForWebPages } }
  ).chrome?.runtime
  return typeof runtime?.sendMessage === "function" ? runtime : undefined
}

export const isKnownExtensionId = (
  extensionId: string | undefined,
): extensionId is string =>
  !!extensionId && TURNUS_EXTENSION_IDS.includes(extensionId)

const sendViaExternallyConnectable = (
  runtime: ChromeRuntimeForWebPages,
  extensionId: string,
  message: unknown,
): Promise<boolean> =>
  new Promise((resolve) => {
    try {
      runtime.sendMessage(extensionId, message, (response) => {
        if (runtime.lastError) {
          console.error(
            "Extension auth handoff failed:",
            runtime.lastError.message,
          )
          resolve(false)
          return
        }
        resolve(
          (response as { success?: boolean } | undefined)?.success === true,
        )
      })
    } catch (error) {
      console.error("Extension auth handoff failed:", error)
      resolve(false)
    }
  })

/**
 * Sends the tokens to the extension. Resolves with the channel used and
 * whether the extension confirmed receipt (postMessage has no receipt and
 * reports true once posted).
 */
export const sendTokensToExtension = async (
  payload: ExtensionAuthPayload,
  extensionId: string | undefined,
): Promise<{ channel: ExtensionHandoffChannel; delivered: boolean }> => {
  const message = { type: "EXTENSION_AUTH_CALLBACK", payload }

  const runtime = getChromeRuntime()
  if (runtime && isKnownExtensionId(extensionId)) {
    // No postMessage fallback on failure: that would put the tokens back on
    // the page's message bus. The extension's content script still picks up
    // the session from storage on its own.
    const delivered = await sendViaExternallyConnectable(
      runtime,
      extensionId,
      message,
    )
    return { channel: "externally_connectable", delivered }
  }

  window.postMessage(message, window.location.origin)
  return { channel: "postMessage", delivered: true }
}
